/*
 * Description:
 * ---------------------
 * Every sentence the Alerts screen says around the alerts themselves.
 *
 * The alerts are worded on the server (server/pkg/alerts/words.go), so what
 * this screen shows is exactly what was emailed. What lives here is the rest:
 * signing up, the headline, the state of each watched line, and who is on each
 * list. The reader may be a customer's office manager, so the words are plain
 * and the headline answers first.
 *
 * ! file components/alertWords.js
 */

export const SIGNUP_TITLE = "Get alerts by email";

/* What signing up gets you, naming the customer when there is only one. */
export function signUpDetail(lists) {
  const only = lists.length === 1 ? lists[0] : null;
  if (only && only.id === "on_call") {
    return "You get an email about every Weavian site when it, or one of its internet lines, stops working or nears its data budget, "
      + "and again when it is back.";
  }
  const which = only ? `one of ${only.name}'s sites` : "a site";
  return `You get an email when ${which}, or one of its internet lines, stops working or nears its data budget, `
    + "and again when it is back.";
}

/* How a sign-up went, from the server's answer. */
export function signedUp(reply, emailOn) {
  if (reply.already) return `${reply.added} is already signed up.`;
  const w = reply.welcome;
  if (w && w.ok) return `Signed up. A confirmation is on its way to ${reply.added}.`;
  if (w) return `Signed up, but the confirmation to ${reply.added} could not be sent: ${w.error}`;
  if (!emailOn) return "Signed up. Email is not set up on this server yet, so alerts start once it is.";
  return "Signed up.";
}

export const NO_LISTS =
  "Your account does not belong to a customer yet, so there is no list to join. Ask a Weavian admin to add you.";

export function emailOff(isSuperAdmin) {
  return isSuperAdmin
    ? "Email is not set up on this server, so nothing is sent yet. Set NABX_SMTP_HOST, NABX_SMTP_FROM and the login on notabox-server."
    : "Email is not set up on this server yet. Addresses are kept, and alerts start once it is.";
}

/* The headline, answering "is anything wrong" before any detail. */
export function headline({ active, sites }) {
  const watched = sites.filter((s) => s.watched).length;
  if (active.length) {
    return {
      tone: "bad",
      title: active.length === 1 ? "One problem right now" : `${active.length} problems right now`,
      detail: "Each clears by itself when the site or line is working again, and everyone signed up is told when it does.",
    };
  }
  if (!watched) {
    return {
      tone: "off",
      title: "No site is being watched",
      detail: "A site is watched once it is assigned to a customer under Access Control.",
    };
  }
  return {
    tone: "good",
    title: "Nothing is wrong right now",
    detail: `Watching ${watched === 1 ? "one site" : `${watched} sites`}. If a site stops working for three minutes, `
      + "or one of its internet lines for five, everyone signed up is told.",
  };
}

export function ago(ts, now = Date.now()) {
  if (!ts) return "never";
  const s = Math.max(0, Math.round((now - new Date(ts).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return m === 1 ? "a minute ago" : `${m} minutes ago`;
  const h = Math.round(m / 60);
  if (h < 48) return h === 1 ? "an hour ago" : `${h} hours ago`;
  return `${Math.round(h / 24)} days ago`;
}

export function sitesSummary(sites) {
  const watched = sites.filter((s) => s.watched).length;
  const not = sites.length - watched;
  return not ? `Sites: ${watched} watched, ${not} not` : `Sites being watched (${watched})`;
}

/* One site's line under the sites disclosure. A site not heard from lately
   says only that: whether its reports are late is beside the point. */
export function siteStatus(site, now = Date.now()) {
  if (!site.watched) return "Not watched. Assign it to a customer under Access Control to watch it.";
  const heard = `Last heard ${ago(site.heard_at, now)}.`;
  if (now - new Date(site.heard_at).getTime() > 60000) return heard;
  if (site.telemetry === "none") {
    return `${heard} It sends no line reports, so only whether it can be reached is watched.`;
  }
  if (site.telemetry === "behind") {
    return `${heard} Its line reports are running late, so its lines are judged again once they catch up.`;
  }
  return heard;
}

export function lineState(state) {
  if (state === "dark") return "not reaching the Weavian cloud";
  if (state === "gone") return "not seen by the box";
  if (state === "unknown") return "not known right now";
  if (state === "off") return "switched off on purpose";
  if (state === "standby") return "working, kept as a backup";
  return "working";
}

/* Green only for a line known to be working now; an old report is grey. */
export function lineTone(state) {
  if (state === "up" || state === "standby") return "up";
  return state === "unknown" || state === "off" ? "unknown" : "down";
}

export const LISTS_DETAIL = "Everyone here is emailed when a problem starts and again when it ends.";
export const ON_CALL_HELP =
  "Weavian's own on-call. Hears every alert, including the two about the server itself and the appliance software.";

export function peopleCount(n) {
  if (!n) return "Nobody yet";
  return n === 1 ? "1 person" : `${n} people`;
}

/* Who put an address on a list, and when. */
export function addedBy(sub, me, now) {
  const when = ago(sub.added_at, now);
  return sub.added_by === me ? `You added this ${when}` : `Added by ${sub.added_by} ${when}`;
}

/* Addresses someone else signed up, which only the list's admins see. */
export function others(n) {
  return n === 1 ? "1 more person was signed up by someone else." : `${n} more people were signed up by others.`;
}

export function empty(list) {
  return list.id === "on_call" ? "Nobody is on call yet." : `Nobody gets alerts about ${list.name} yet.`;
}

export function removed(to) {
  return `${recipientLabel(to)} will get no more alerts.`;
}

export const WEBHOOK_HELP =
  "Paste an incoming webhook address from Slack, Teams or another chat app, and alerts post there too.";

export function hookAdded(reply) {
  const w = reply.welcome;
  if (reply.already) return { ok: true, text: "That chat app is already on the list." };
  if (w && !w.ok) return { ok: false, text: `Added, but the test post failed: ${w.error}` };
  return { ok: true, text: "Added. A test post is on its way to it." };
}

/* A webhook's path is its credential: say where it goes, not the whole URL. */
export function recipientLabel(to) {
  if (!to.startsWith("https://")) return to;
  try {
    return `${new URL(to).host}/…`;
  } catch {
    return "webhook";
  }
}

/* How the last message to one recipient went. */
export function lastResult(r, now) {
  if (!r) return "";
  return r.ok ? `Last sent ${ago(r.at, now)}` : `Last message failed ${ago(r.at, now)}: ${r.error}`;
}

export function testOutcome(results) {
  const failed = results.filter((r) => !r.ok);
  if (!failed.length) {
    return { ok: true, text: `Test sent to ${results.length === 1 ? "the one recipient" : `all ${results.length} recipients`}.` };
  }
  return {
    ok: false,
    text: `Not delivered to ${failed.map((r) => `${recipientLabel(r.to)} (${r.error})`).join(", ")}.`,
  };
}

export const NOT_ON_SERVER =
  "This Weavian server does not have alerts yet. They arrive with its next update.";
